<template>
  <PageLayout
    :is-loading="isLoading"
    page-title="Campaign Details"
    :show-back-button="true"
    :show-banner="false"
    :show-sponsors="false"
    :hide-sidebar="true"
    @back="goBack"
  >
    <!-- Campaign Header Section -->
    <div class="campaign-detail-section">
      <div class="campaign-header">
        <div class="campaign-image-container">
          <img 
            :src="campaign.logo_brand || require('@/assets/cta-background.png')" 
            :alt="campaign.name" 
            class="campaign-main-image" 
          />
        </div>
        
        <div class="campaign-info">
          <div class="campaign-category">{{ campaign.category }}</div>
          <h1 class="campaign-title">{{ campaign.name }}</h1>
          
          <!-- Campaign Details List -->
          <div class="campaign-details-list">
            <div class="detail-item" @click="toggleSection('description')">
              <div class="detail-label">Deskripsi Campaign</div>
              <div class="detail-icon">
                <i class="mdi mdi-chevron-right" :class="{ 'rotated': activeSection === 'description' }"></i>
              </div>
            </div>
            
            <div class="detail-item" @click="toggleSection('tasks')">
              <div class="detail-label">Tugas</div>
              <div class="detail-icon">
                <i class="mdi mdi-chevron-right" :class="{ 'rotated': activeSection === 'tasks' }"></i>
              </div>
            </div>
            
            <div class="detail-item" @click="toggleSection('points')">
              <div class="detail-label">Total Points</div>
              <div class="detail-icon">
                <i class="mdi mdi-chevron-right" :class="{ 'rotated': activeSection === 'points' }"></i>
              </div>
            </div>
            
            <div class="detail-item" @click="toggleSection('requirements')">
              <div class="detail-label">Syarat dan Ketentuan</div>
              <div class="detail-icon">
                <i class="mdi mdi-chevron-right" :class="{ 'rotated': activeSection === 'requirements' }"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Expandable Content Sections -->
    <div v-if="activeSection" class="content-section">
      <!-- Description Section -->
      <div v-if="activeSection === 'description'" class="content-card">
        <h3 class="content-title">Deskripsi Campaign</h3>
        <div class="content-body" v-html="campaign.description"></div>
      </div>
      
      <!-- Tasks Section -->
      <div v-if="activeSection === 'tasks'" class="content-card">
        <h3 class="content-title">Tugas Campaign</h3>
        <div class="content-body">
          <div class="task-info">
            <div class="info-row">
              <span class="info-label">Tipe Project:</span>
              <span class="info-value">{{ campaign.tipe_project }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Platform:</span>
              <span class="info-value">{{ campaign.platform }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Timeline:</span>
              <span class="info-value">{{ formatDateRange(campaign.start_reg, campaign.end_reg) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Kuota:</span>
              <span class="info-value">{{ campaign.kuota }} Partisipan</span>
            </div>
          </div>
          <div class="sow-content">
            <h4>Scope of Work:</h4>
            <div v-html="campaign.sow_campaign"></div>
          </div>
        </div>
      </div>
      
      <!-- Points Section -->
      <div v-if="activeSection === 'points'" class="content-card">
        <h3 class="content-title">Benefit & Rewards</h3>
        <div class="content-body">
          <div class="benefits-grid">
            <div v-if="campaign.benefit_point" class="benefit-card">
              <div class="benefit-icon">
                <i class="mdi mdi-star"></i>
              </div>
              <div class="benefit-info">
                <div class="benefit-value">{{ campaign.benefit_point }}</div>
                <div class="benefit-label">Points</div>
              </div>
            </div>
            
            <div v-if="campaign.benefit_fee" class="benefit-card">
              <div class="benefit-icon">
                <i class="mdi mdi-cash"></i>
              </div>
              <div class="benefit-info">
                <div class="benefit-value">Fee</div>
                <div class="benefit-label">Tersedia</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Requirements Section -->
      <div v-if="activeSection === 'requirements'" class="content-card">
        <h3 class="content-title">Syarat dan Ketentuan</h3>
        <div class="content-body">
          <div class="requirements-content" v-html="formattedKriteria"></div>
        </div>
      </div>
    </div>

    <!-- Campaign Status Section -->
    <div v-if="userCampaign.status" class="status-section">
      <div class="status-card" :class="getStatusClass(userCampaign.status)">
        <div class="status-icon">
          <i :class="getStatusIcon(userCampaign.status)"></i>
        </div>
        <div class="status-content">
          <h3 class="status-title">{{ getStatusTitle(userCampaign.status) }}</h3>
          <p class="status-description">{{ getStatusDescription(userCampaign.status) }}</p>
        </div>
      </div>
      
      <!-- WhatsApp Group Link (for APPROVED or STORYLINE SUBMITTED status) -->
      <div v-if="(userCampaign.status === 'APPROVED' || userCampaign.status === 'STORYLINE SUBMITTED') && campaign.whatsapp_group" class="whatsapp-section">
        <a :href="campaign.whatsapp_group" target="_blank" class="whatsapp-button">
          <i class="mdi mdi-whatsapp"></i>
          Bergabung ke WhatsApp Grup Campaign
        </a>
      </div>
    </div>

    <!-- Campaign Timeline (for approved campaigns) -->
    <div v-if="userCampaign.status && userCampaign.status !== 'PENDING' && userCampaign.status !== 'REJECTED'" class="timeline-section">
      <div class="timeline-card">
        <h3 class="timeline-title">Timeline Campaign</h3>
        <div class="timeline-list">
          <div class="timeline-item">
            <div class="timeline-label">Submit Draft:</div>
            <div class="timeline-value">{{ campaign.submit_draft || '-' }}</div>
          </div>
          <div class="timeline-item">
            <div class="timeline-label">Preview Draft:</div>
            <div class="timeline-value">{{ campaign.preview_draft || '-' }}</div>
          </div>
          <div class="timeline-item">
            <div class="timeline-label">Upload Konten:</div>
            <div class="timeline-value">{{ campaign.upload_konten || '-' }}</div>
          </div>
          <div class="timeline-item">
            <div class="timeline-label">Bukti Tayang:</div>
            <div class="timeline-value">{{ campaign.bukti_tayang || '-' }}</div>
          </div>
          <div class="timeline-item">
            <div class="timeline-label">Bukti Insight:</div>
            <div class="timeline-value">{{ campaign.bukti_insight || '-' }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Product Knowledge Download (for APPROVED or STORYLINE SUBMITTED) -->
    <div v-if="(userCampaign.status === 'APPROVED' || userCampaign.status === 'STORYLINE SUBMITTED') && campaign.product_knowledge" class="product-knowledge-section">
      <div class="knowledge-card">
        <div class="knowledge-header">
          <h3 class="knowledge-title">Brief & Product Knowledge</h3>
          <button class="download-button" @click="downloadKnowledge(campaign.product_knowledge)">
            <i class="mdi mdi-download"></i>
            Download
          </button>
        </div>
      </div>
    </div>

    <!-- Storyline Submission Form (for APPROVED status) -->
    <div v-if="userCampaign.status === 'APPROVED'" class="submission-section">
      <div class="submission-card">
        <h3 class="submission-title">Submit Storyline</h3>
        <form @submit.prevent="submitStoryline">
          <div class="form-group">
            <label class="form-label">Silahkan tulis storyline yang akan digunakan:</label>
            <textarea 
              v-model="storyline" 
              class="form-textarea" 
              rows="6" 
              placeholder="Tulis storyline Anda di sini..."
              required
            ></textarea>
          </div>
          <button 
            type="submit" 
            class="submit-button" 
            :disabled="!campaign.product_knowledge || isLoading"
          >
            {{ isLoading ? 'Mengirim...' : 'Submit Storyline' }}
          </button>
        </form>
      </div>
    </div>

    <!-- Caption & Draft Submission Form (for STORYLINE SUBMITTED status) -->
    <div v-if="userCampaign.status === 'STORYLINE SUBMITTED'" class="submission-section">
      <div class="submission-card">
        <h3 class="submission-title">Submit Caption & Draft Konten</h3>
        <form @submit.prevent="submitCaption">
          <div class="form-group">
            <label class="form-label">Silahkan tulis caption yang akan digunakan:</label>
            <textarea 
              v-model="caption" 
              class="form-textarea" 
              rows="6" 
              placeholder="Tulis caption Anda di sini..."
              required
            ></textarea>
          </div>
          <div class="form-group">
            <label class="form-label">Upload draft konten:</label>
            <input 
              type="file" 
              @change="handleFileChangeDraft($event)" 
              class="file-input"
              required
            />
          </div>
          <button 
            type="submit" 
            class="submit-button" 
            :disabled="!campaign.product_knowledge || isLoading"
          >
            {{ isLoading ? 'Mengirim...' : 'Submit Draft' }}
          </button>
        </form>
      </div>
    </div>

    <!-- Revision Feedback & Form (for DRAFT PERLU REVISI status) -->
    <div v-if="userCampaign.status === 'DRAFT PERLU REVISI'" class="submission-section">
      <div class="feedback-card">
        <h3 class="feedback-title">Feedback untuk Draft Sebelumnya</h3>
        <div class="feedback-content">
          {{ userCampaign.feedback_draft || 'Tidak ada feedback' }}
        </div>
      </div>
      
      <div class="submission-card">
        <h3 class="submission-title">Submit Revisi</h3>
        <form @submit.prevent="submitRevisi">
          <div class="form-group">
            <label class="form-label">Silahkan tulis revisi caption:</label>
            <textarea 
              v-model="caption" 
              class="form-textarea" 
              rows="6" 
              placeholder="Tulis revisi caption Anda di sini..."
              required
            ></textarea>
          </div>
          <div class="form-group">
            <label class="form-label">Upload revisi konten:</label>
            <input 
              type="file" 
              @change="handleFileChangeDraft($event)" 
              class="file-input"
              required
            />
          </div>
          <button 
            type="submit" 
            class="submit-button" 
            :disabled="isLoading"
          >
            {{ isLoading ? 'Mengirim...' : 'Submit Revisi' }}
          </button>
        </form>
      </div>
    </div>

    <!-- Draft Under Review (for DRAFT SUBMITTED or REVISI SUBMITTED) -->
    <div v-if="userCampaign.status === 'DRAFT SUBMITTED' || userCampaign.status === 'REVISI SUBMITTED'" class="info-section">
      <div class="info-card">
        <div class="info-icon">
          <i class="mdi mdi-clock-outline"></i>
        </div>
        <div class="info-content">
          <h3 class="info-title">File Sedang Direview</h3>
          <p class="info-description">Draft Anda sedang dalam proses review. Mohon tunggu konfirmasi dari tim kami.</p>
        </div>
      </div>
    </div>

    <!-- Content Approved - Bukti Tayang Form (for CONTENT APPROVED status) -->
    <div v-if="userCampaign.status === 'CONTENT APPROVED'" class="submission-section">
      <div class="success-card">
        <div class="success-icon">
          <i class="mdi mdi-check-circle"></i>
        </div>
        <div class="success-content">
          <h3 class="success-title">Draft Approved!</h3>
          <p class="success-description">Selamat draft kamu sudah Approved! Jangan lupa upload sesuai timeline yang sudah ditentukan!</p>
        </div>
      </div>
      
      <div class="submission-card">
        <h3 class="submission-title">Submit Bukti Tayang</h3>
        <form @submit.prevent="submitBuktiTayang">
          <div class="form-group">
            <label class="form-label">Submit link konten disini:</label>
            <textarea 
              v-model="caption" 
              class="form-textarea" 
              rows="3" 
              placeholder="Paste link konten Anda di sini..."
              required
            ></textarea>
          </div>
          <div class="form-group">
            <label class="form-label">Upload bukti tayang:</label>
            <input 
              type="file" 
              @change="handleFileChangeDraft($event)" 
              class="file-input"
              required
            />
          </div>
          <button 
            type="submit" 
            class="submit-button" 
            :disabled="isLoading"
          >
            {{ isLoading ? 'Mengirim...' : 'Submit Bukti Tayang' }}
          </button>
        </form>
      </div>
    </div>

    <!-- Insight Submission Form (for BUKTI TAYANG SUBMITTED status) -->
    <div v-if="userCampaign.status === 'BUKTI TAYANG SUBMITTED'" class="submission-section">
      <div class="submission-card">
        <h3 class="submission-title">Submit Insight</h3>
        <form @submit.prevent="submitInsight">
          <!-- Dynamic Questions -->
          <div v-if="campaign.question && campaign.question.length > 0" class="questions-section">
            <h4 class="questions-title">Pertanyaan Campaign:</h4>
            <div v-for="question in campaign.question" :key="question.campaign_question_id" class="form-group">
              <label class="form-label">{{ question.question }}</label>
              
              <div v-if="question.tipe === 'file'">
                <input 
                  type="file" 
                  @change="handleFileChange(question.campaign_question_id, $event)"
                  class="file-input"
                />
              </div>
              <div v-else-if="question.tipe === 'select'">
                <select 
                  v-model="question.value"
                  @change="updateFormData(question.campaign_question_id, question.value)"
                  class="form-select"
                >
                  <option value="">Pilih opsi</option>
                  <option 
                    v-for="option in question.answer.split(',')" 
                    :key="option" 
                    :value="option.trim()"
                  >
                    {{ option.trim() }}
                  </option>
                </select>
              </div>
              <div v-else>
                <input 
                  :type="question.tipe"
                  v-model="question.value"
                  @change="updateFormData(question.campaign_question_id, question.value)"
                  class="form-input"
                />
              </div>
            </div>
          </div>

          <!-- Insight Metrics -->
          <div class="metrics-section">
            <h4 class="metrics-title">Metrics Insight:</h4>
            
            <div class="metrics-grid">
              <div class="form-group">
                <label class="form-label">Total Engagement:</label>
                <input 
                  type="number" 
                  v-model="insightData.total_engagement" 
                  class="form-input"
                  required
                />
              </div>
              
              <div class="form-group">
                <label class="form-label">Total Impression:</label>
                <input 
                  type="number" 
                  v-model="insightData.total_impression" 
                  class="form-input"
                  required
                />
              </div>
              
              <div class="form-group">
                <label class="form-label">Total Reach:</label>
                <input 
                  type="number" 
                  v-model="insightData.reach" 
                  class="form-input"
                  required
                />
              </div>
              
              <div class="form-group">
                <label class="form-label">Likes:</label>
                <input 
                  type="number" 
                  v-model="insightData.likes" 
                  class="form-input"
                  required
                />
              </div>
              
              <div class="form-group">
                <label class="form-label">Comment:</label>
                <input 
                  type="number" 
                  v-model="insightData.comment" 
                  class="form-input"
                  required
                />
              </div>
              
              <div class="form-group">
                <label class="form-label">Share:</label>
                <input 
                  type="number" 
                  v-model="insightData.share" 
                  class="form-input"
                  required
                />
              </div>
              
              <div class="form-group">
                <label class="form-label">Save:</label>
                <input 
                  type="number" 
                  v-model="insightData.save" 
                  class="form-input"
                  required
                />
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Upload Insight (Multiple Files):</label>
            <input 
              type="file" 
              @change="handleFileChangeMultiple($event)" 
              class="file-input"
              multiple
              required
            />
          </div>
          
          <button 
            type="submit" 
            class="submit-button" 
            :disabled="isLoading"
          >
            {{ isLoading ? 'Mengirim...' : 'Submit Insight' }}
          </button>
        </form>
      </div>
    </div>

    <!-- Campaign Completed (for BUKTI INSIGHT SUBMITTED status) -->
    <div v-if="userCampaign.status === 'BUKTI INSIGHT SUBMITTED'" class="completion-section">
      <div class="completion-card">
        <div class="completion-icon">
          <i class="mdi mdi-trophy"></i>
        </div>
        <div class="completion-content">
          <h3 class="completion-title">Terima Kasih!</h3>
          <h4 class="completion-subtitle">Campaign Selesai</h4>
          <p class="completion-description">
            Campaign ini telah selesai. Untuk mengikuti berbagai campaign lain, silakan klik tombol di bawah.
          </p>
          <button class="back-to-campaign-button" @click="$router.push('/user/campaign')">
            Kembali ke Halaman Campaign
          </button>
        </div>
      </div>
    </div>

    <!-- Registration Form Section -->
    <div v-if="!userCampaign.status" class="registration-section">
      <div class="registration-card">
        <h3 class="registration-title">Daftar Campaign</h3>
        
        <!-- Toggle between Detail and Form -->
        <div class="toggle-buttons">
          <button 
            class="toggle-btn" 
            :class="{ active: showDetail }" 
            @click="toggleView('detail')"
          >
            Detail Campaign
          </button>
          <button 
            class="toggle-btn" 
            :class="{ active: showForm }" 
            @click="toggleView('form')"
          >
            Form Pendaftaran
          </button>
        </div>

        <!-- Detail View -->
        <div v-if="showDetail" class="detail-view">
          <div class="detail-section">
            <label class="detail-label">Kriteria:</label>
            <div class="detail-content" v-html="formattedKriteria"></div>
          </div>

          <div class="benefits-row">
            <div v-if="campaign.benefit_point && campaign.benefit_point !== 0" class="benefit-box">
              <label class="detail-label">Benefit Point:</label>
              <div class="benefit-value">{{ campaign.benefit_point }} Points</div>
            </div>
            <div v-if="campaign.benefit_fee && campaign.benefit_fee !== 0" class="benefit-box">
              <label class="detail-label">Benefit Fee:</label>
              <div class="benefit-value">Fee Tersedia</div>
            </div>
          </div>

          <div class="detail-section">
            <label class="detail-label">Scope of Work:</label>
            <div class="detail-content" v-html="campaign.sow_campaign"></div>
          </div>

          <div class="detail-section">
            <label class="detail-label">Timeline:</label>
            <div class="detail-content">{{ formatDateRange(campaign.start_reg, campaign.end_reg) }}</div>
          </div>
        </div>

        <!-- Form View -->
        <div v-if="showForm" class="form-view">
          <form @submit.prevent="submitRegistration">
            <div v-for="question in campaign.question" :key="question.campaign_question_id" class="form-group">
              <label class="form-label">{{ question.question }}</label>
              
              <!-- File Input -->
              <div v-if="question.tipe === 'file'" class="file-input-wrapper">
                <input 
                  type="file" 
                  @change="handleFileChange(question.campaign_question_id, $event)"
                  class="file-input"
                />
              </div>

              <!-- Select Input -->
              <div v-else-if="question.tipe === 'select'">
                <select 
                  v-model="question.value"
                  @change="updateFormData(question.campaign_question_id, question.value)"
                  class="form-select"
                  required
                >
                  <option value="">Pilih opsi</option>
                  <option 
                    v-for="option in question.answer.split(',')" 
                    :key="option" 
                    :value="option.trim()"
                  >
                    {{ option.trim() }}
                  </option>
                </select>
              </div>

              <!-- Text/Number/Email Input -->
              <div v-else>
                <input 
                  :type="question.tipe"
                  v-model="question.value"
                  @change="updateFormData(question.campaign_question_id, question.value)"
                  class="form-input"
                  required
                />
              </div>
            </div>

            <!-- Terms and Conditions -->
            <div class="terms-section">
              <label class="checkbox-label">
                <input type="checkbox" v-model="agreeTerms" />
                <span>Saya menyetujui syarat & ketentuan pendaftaran campaign</span>
              </label>
            </div>

            <!-- Submit Button -->
            <button 
              type="submit" 
              class="submit-button" 
              :disabled="!agreeTerms || isLoading"
            >
              {{ isLoading ? 'Mendaftar...' : 'Daftar Campaign' }}
            </button>
          </form>
        </div>
      </div>
    </div>

  </PageLayout>
</template>

<script>
import AppPageLayout from '@/components/ui/PageLayout.vue';

export default {
  name: "NewCampaignDetail",
  components: { 
    PageLayout: AppPageLayout
  },
  data() {
    return {
      isLoading: false,
      campaign: {},
      userCampaign: {},
      detailUser: {},
      activeSection: null,
      agreeTerms: false,
      campaignId: null,
      showDetail: true,
      showForm: false,
      formData: [],
      storyline: '',
      caption: '',
      fileDraft: null,
      fileMultiple: [],
      insightData: {
        total_engagement: '',
        total_impression: '',
        reach: '',
        likes: '',
        comment: '',
        share: '',
        save: ''
      }
    };
  },
  computed: {
    formattedKriteria() {
      if (!this.campaign.kriteria) return '';
      
      // Replace line breaks with <br> tags and preserve formatting
      return this.campaign.kriteria
        .replace(/\n/g, '<br>')
        .replace(/\r/g, '');
    }
  },
  methods: {
    loadCampaignDetail() {
      this.campaignId = this.$route.params.id;
      const baseURI = this.$settings.endPoint + "campaign/detail-user/" + this.campaignId;
      this.isLoading = true;
      
      return this.$http
        .get(baseURI, { 
          timeout: 100000,
          'headers': {
            'AppToken': this.$settings.jwtSecret,
          }
        })
        .then((response) => {
          this.isLoading = false;
          this.campaign = response.data.datas;
          
          // Initialize form data with existing answers if available
          if (this.campaign.question && this.campaign.question.length > 0) {
            this.campaign.question.forEach(question => {
              question.value = '';
              
              // If there are existing answers, populate them
              if (response.data.datas.answer && response.data.datas.answer.length > 0) {
                const existingAnswer = response.data.datas.answer.find(
                  ans => ans.campaign_question_id === question.campaign_question_id
                );
                if (existingAnswer) {
                  question.value = existingAnswer.answer;
                  this.formData.push({
                    campaign_question_id: question.campaign_question_id,
                    answer: existingAnswer.answer
                  });
                }
              }
            });
          }
          
          // Check if user is registered for this campaign
          if (response.data.datas.user && response.data.datas.user.length > 0) {
            this.userCampaign = response.data.datas.user[0];
          }
        })
        .catch(error => {
          this.isLoading = false;
          console.log('Error:', error);
          this.handleError(error);
        });
    },
    
    toggleView(view) {
      if (view === 'detail') {
        this.showDetail = true;
        this.showForm = false;
      } else {
        this.showDetail = false;
        this.showForm = true;
      }
    },
    
    handleFileChange(questionId, event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Remove existing entry for this question
      const index = this.formData.findIndex(item => item.campaign_question_id === questionId);
      if (index !== -1) {
        this.formData.splice(index, 1);
      }
      
      // Add new entry
      this.formData.push({
        campaign_question_id: questionId,
        answer: file
      });
    },
    
    updateFormData(questionId, value) {
      // Remove existing entry for this question
      const index = this.formData.findIndex(item => item.campaign_question_id === questionId);
      if (index !== -1) {
        this.formData.splice(index, 1);
      }
      
      // Add new entry
      this.formData.push({
        campaign_question_id: questionId,
        answer: value
      });
    },
    
    submitRegistration() {
      if (!this.agreeTerms) {
        this.error('Anda harus menyetujui syarat & ketentuan');
        return;
      }
      
      this.isLoading = true;
      let formData = new FormData();
      
      formData.append("campaign_id", this.campaign.campaign_id);
      
      // Append all form answers
      this.formData.forEach((item, index) => {
        formData.append(`answer[${index}][campaign_question_id]`, item.campaign_question_id);
        formData.append(`answer[${index}][answer]`, item.answer);
      });
      
      const baseURI = this.$settings.endPoint + "campaign-user/create";
      
      this.$http
        .post(baseURI, formData, { 
          timeout: 100000,
          'headers': {
            'AppToken': this.$settings.jwtSecret,
          }
        })
        .then((response) => {
          this.isLoading = false;
          if (response.data.status === 200) {
            this.success('Berhasil mendaftar campaign!');
            // Reload campaign detail to show updated status
            this.loadCampaignDetail();
          } else {
            this.error('Gagal mendaftar campaign');
          }
        })
        .catch(error => {
          this.isLoading = false;
          if (error.response && error.response.data && error.response.data.errors) {
            this.resultError(error.response.data.errors);
          } else {
            this.error('Terjadi kesalahan saat mendaftar');
          }
        });
    },
    
    toggleSection(section) {
      if (this.activeSection === section) {
        this.activeSection = null;
      } else {
        this.activeSection = section;
      }
    },
    
    registerCampaign() {
      if (!this.agreeTerms) return;
      
      // Check if user profile is complete
      if (this.detailUser.jenis_kelamin === null || this.detailUser.jenis_kelamin === undefined) {
        const baseURI = this.$settings.endPoint + "pengguna/check-reg/" + this.detailUser.user_id;
        return this.$http
          .get(baseURI, { timeout: 100000 })
          .then((response) => {
            let cek = response.data.datas;
            if (cek !== 'ok') {
              this.$swal({
                title: 'Warning',
                text: 'Mohon lengkapi terlebih dahulu profile anda',
                showCancelButton: false,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes!",
              }).then(() => {
                this.$router.push("/user/profile/edit");
              });
              return;
            }
            this.submitRegistration();
          })
          .catch(error => {
            this.handleError(error);
          });
      } else {
        this.submitRegistration();
      }
    },
    
    resultError(data) {
      var count = Object.keys(data).length;
      for (var x = 0; x < count; x++) {
        var nameOb = Object.keys(data)[x];
        var objectData = data[nameOb];
        for (var y = 0; y < objectData.length; y++) {
          this.error(objectData[y]);
        }
      }
    },
    
    handleFileChangeDraft(event) {
      const file = event.target.files[0];
      this.fileDraft = file;
    },
    
    handleFileChangeMultiple(event) {
      this.fileMultiple = event.target.files;
    },
    
    downloadKnowledge(url) {
      if (!url) return;
      
      this.$http.get(url, { responseType: 'blob' })
        .then(response => {
          const contentType = response.headers['content-type'];
          const fileExtension = this.getFileExtension(contentType);
          const filename = `product_knowledge.${fileExtension || 'pdf'}`;
          
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          this.success('File berhasil didownload');
        })
        .catch(error => {
          console.error('Download error:', error);
          this.error('Gagal mendownload file');
        });
    },
    
    getFileExtension(contentType) {
      if (contentType.includes('pdf')) return 'pdf';
      if (contentType.includes('image/jpeg')) return 'jpg';
      if (contentType.includes('image/png')) return 'png';
      if (contentType.includes('doc') || contentType.includes('word')) return 'doc';
      return null;
    },
    
    submitStoryline() {
      if (!this.storyline) {
        this.error('Storyline tidak boleh kosong');
        return;
      }
      
      this.isLoading = true;
      let formData = new FormData();
      formData.append("storyline", this.storyline);
      
      const baseURI = this.$settings.endPoint + "campaign-user/update-storyline/" + this.userCampaign.campaign_user_id;
      
      this.$http
        .post(baseURI, formData, { 
          timeout: 200000,
          'headers': {
            'AppToken': this.$settings.jwtSecret,
          }
        })
        .then((response) => {
          this.isLoading = false;
          if (response.data.status === 200) {
            this.success('Storyline berhasil disubmit!');
            this.storyline = '';
            this.loadCampaignDetail();
          } else {
            this.error('Gagal submit storyline');
          }
        })
        .catch(error => {
          this.isLoading = false;
          this.handleError(error);
        });
    },
    
    submitCaption() {
      if (!this.caption || !this.fileDraft) {
        this.error('Caption dan file draft harus diisi');
        return;
      }
      
      this.isLoading = true;
      let formData = new FormData();
      formData.append("caption", this.caption);
      formData.append("file_draft", this.fileDraft);
      
      const baseURI = this.$settings.endPoint + "campaign-user/update/" + this.userCampaign.campaign_user_id;
      
      this.$http
        .post(baseURI, formData, { 
          timeout: 200000,
          'headers': {
            'AppToken': this.$settings.jwtSecret,
          }
        })
        .then((response) => {
          this.isLoading = false;
          if (response.data.status === 200) {
            this.success('Caption dan draft berhasil disubmit!');
            this.caption = '';
            this.fileDraft = null;
            this.loadCampaignDetail();
          } else {
            this.error('Gagal submit caption dan draft');
          }
        })
        .catch(error => {
          this.isLoading = false;
          this.handleError(error);
        });
    },
    
    submitRevisi() {
      if (!this.caption || !this.fileDraft) {
        this.error('Revisi caption dan file harus diisi');
        return;
      }
      
      this.isLoading = true;
      let formData = new FormData();
      formData.append("revisi_caption", this.caption);
      formData.append("revisi_draft", this.fileDraft);
      
      const baseURI = this.$settings.endPoint + "campaign-user/update-revisi/" + this.userCampaign.campaign_user_id;
      
      this.$http
        .post(baseURI, formData, { 
          timeout: 200000,
          'headers': {
            'AppToken': this.$settings.jwtSecret,
          }
        })
        .then((response) => {
          this.isLoading = false;
          if (response.data.status === 200) {
            this.success('Revisi berhasil disubmit!');
            this.caption = '';
            this.fileDraft = null;
            this.loadCampaignDetail();
          } else {
            this.error('Gagal submit revisi');
          }
        })
        .catch(error => {
          this.isLoading = false;
          this.handleError(error);
        });
    },
    
    submitBuktiTayang() {
      if (!this.caption || !this.fileDraft) {
        this.error('Link konten dan bukti tayang harus diisi');
        return;
      }
      
      this.isLoading = true;
      let formData = new FormData();
      formData.append("link_konten", this.caption);
      formData.append("bukti_tayang", this.fileDraft);
      
      const baseURI = this.$settings.endPoint + "campaign-user/update-bukti-tayang/" + this.userCampaign.campaign_user_id;
      
      this.$http
        .post(baseURI, formData, { 
          timeout: 200000,
          'headers': {
            'AppToken': this.$settings.jwtSecret,
          }
        })
        .then((response) => {
          this.isLoading = false;
          if (response.data.status === 200) {
            this.success('Bukti tayang berhasil disubmit!');
            this.caption = '';
            this.fileDraft = null;
            this.loadCampaignDetail();
          } else {
            this.error('Gagal submit bukti tayang');
          }
        })
        .catch(error => {
          this.isLoading = false;
          this.handleError(error);
        });
    },
    
    submitInsight() {
      // Validate insight data
      if (!this.insightData.total_engagement || !this.insightData.total_impression || 
          !this.insightData.reach || !this.insightData.likes || !this.insightData.comment || 
          !this.insightData.share || !this.insightData.save) {
        this.error('Semua field metrics harus diisi');
        return;
      }
      
      if (!this.fileMultiple || this.fileMultiple.length === 0) {
        this.error('File insight harus diupload');
        return;
      }
      
      this.isLoading = true;
      let formData = new FormData();
      
      formData.append("campaign_id", this.campaign.campaign_id);
      formData.append("total_engagement", this.insightData.total_engagement);
      formData.append("total_impression", this.insightData.total_impression);
      formData.append("likes", this.insightData.likes);
      formData.append("reach", this.insightData.reach);
      formData.append("comment", this.insightData.comment);
      formData.append("share", this.insightData.share);
      formData.append("save", this.insightData.save);
      
      // Append form answers
      this.formData.forEach((item, index) => {
        formData.append(`answer[${index}][campaign_question_id]`, item.campaign_question_id);
        formData.append(`answer[${index}][answer]`, item.answer);
      });
      
      // Append multiple files
      Array.from(this.fileMultiple).forEach((file, index) => {
        formData.append(`files[${index}]`, file);
      });
      
      const baseURI = this.$settings.endPoint + "campaign-user/update-insight/" + this.userCampaign.campaign_user_id;
      
      this.$http
        .post(baseURI, formData, { 
          timeout: 200000,
          'headers': {
            'AppToken': this.$settings.jwtSecret,
          }
        })
        .then((response) => {
          this.isLoading = false;
          if (response.data.status === 200) {
            this.success('Insight berhasil disubmit!');
            // Redirect to campaign detail with success message
            this.$router.push(`/user/campaign-detail/${this.campaignId}?status=200&msg=Successfully`);
            this.loadCampaignDetail();
          } else {
            this.error('Gagal submit insight');
          }
        })
        .catch(error => {
          this.isLoading = false;
          this.handleError(error);
        });
    },
    
    getStatusClass(status) {
      const statusClasses = {
        'PENDING': 'status-pending',
        'APPROVED': 'status-approved',
        'REJECTED': 'status-rejected',
        'COMPLETED': 'status-completed'
      };
      return statusClasses[status] || 'status-default';
    },
    
    getStatusIcon(status) {
      const statusIcons = {
        'PENDING': 'mdi mdi-clock-outline',
        'APPROVED': 'mdi mdi-check-circle',
        'REJECTED': 'mdi mdi-close-circle',
        'COMPLETED': 'mdi mdi-trophy'
      };
      return statusIcons[status] || 'mdi mdi-information';
    },
    
    getStatusTitle(status) {
      const statusTitles = {
        'PENDING': 'Menunggu Persetujuan',
        'APPROVED': 'Campaign Disetujui',
        'REJECTED': 'Campaign Ditolak',
        'COMPLETED': 'Campaign Selesai'
      };
      return statusTitles[status] || 'Status Campaign';
    },
    
    getStatusDescription(status) {
      const statusDescriptions = {
        'PENDING': 'Profilmu sedang kami seleksi untuk campaign ini.',
        'APPROVED': 'Selamat! Kamu lolos mengikuti campaign ini.',
        'REJECTED': 'Mohon maaf kamu belum berhasil lolos pada campaign ini.',
        'COMPLETED': 'Terima kasih telah menyelesaikan campaign ini!'
      };
      return statusDescriptions[status] || 'Status campaign sedang diproses.';
    },
    
    formatDateRange(startDate, endDate) {
      if (!startDate || !endDate) return '';
      const start = new Date(startDate).toLocaleDateString('id-ID', { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
      const end = new Date(endDate).toLocaleDateString('id-ID', { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
      return `${start} - ${end}`;
    },
    
    goBack() {
      this.$router.go(-1);
    },
    
    goToCampaignHistory() {
      this.$router.push("/user/campaign-history");
    },
    
    fetchUserData() {
      const userDatas = this.$getUserInfo();
      this.detailUser = userDatas.sub;
    },
    
    success(message) {
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", this.$swal.stopTimer);
          toast.addEventListener("mouseleave", this.$swal.resumeTimer);
        },
      });
      Toast.fire({
        icon: "success",
        title: message,
      });
    },
    
    error(message) {
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", this.$swal.stopTimer);
          toast.addEventListener("mouseleave", this.$swal.resumeTimer);
        },
      });
      Toast.fire({
        icon: "error",
        title: message,
      });
    },
    
    handleError(error) {
      if (error.response) {
        if (error.response.status === 422) {
          console.error('Validation error:', error.response.data.errors);
        } else if (error.response.status === 500) {
          this.$router.push('/server-error');
        } else if (error.response.status === 401) {
          this.$router.push("/authorized-error");
        } else {
          this.$router.push('/page-not-found');
        }
      }
    },
  },
  mounted() {
    this.fetchUserData();
    this.loadCampaignDetail();
  }
};
</script>

<style scoped>
/* ========================================= */
/* CAMPAIGN DETAIL SECTION                   */
/* ========================================= */
.campaign-detail-section {
  padding: 24px 32px;
  margin: 0 auto 32px auto;
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  max-width: 1200px;
  width: calc(100% - 64px);
}

.campaign-header {
  display: flex;
  gap: 32px;
  align-items: flex-start;
}

.campaign-image-container {
  width: 400px;
  height: 300px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
}

.campaign-main-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.campaign-info {
  flex: 1;
}

.campaign-category {
  background: #F2994A;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  display: inline-block;
  margin-bottom: 12px;
}

.campaign-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1F2937;
  margin: 0 0 24px 0;
  line-height: 1.2;
}

.campaign-details-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #F8F9FA;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.detail-item:hover {
  background: #E5F3FF;
  border-color: #4A90E2;
}

.detail-label {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
}

.detail-icon {
  color: #F2994A;
  font-size: 1.2rem;
  transition: transform 0.3s ease;
}

.detail-icon .rotated {
  transform: rotate(90deg);
}

/* ========================================= */
/* CONTENT SECTIONS                           */
/* ========================================= */
.content-section {
  padding: 0 32px;
  margin: 0 auto 32px auto;
  max-width: 1200px;
  width: calc(100% - 64px);
}

.content-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  border-left: 4px solid #F2994A;
}

.content-title {
  background: #F8F9FA;
  padding: 20px 24px;
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1F2937;
  border-bottom: 1px solid #E5E7EB;
}

.content-body {
  padding: 24px;
}

.requirements-content {
  line-height: 1.8;
  color: #374151;
  font-size: 0.95rem;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.requirements-content::v-deep br {
  display: block;
  content: "";
  margin: 0.5em 0;
}

.task-info {
  margin-bottom: 24px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #F3F4F6;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: 600;
  color: #374151;
}

.info-value {
  color: #6B7280;
  text-align: right;
}

.sow-content h4 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1F2937;
  margin: 0 0 12px 0;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.benefit-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, #4A90E2 0%, #357ABD 100%);
  border-radius: 12px;
  color: white;
}

.benefit-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.benefit-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
}

.benefit-label {
  font-size: 0.9rem;
  opacity: 0.9;
}

/* ========================================= */
/* STATUS SECTION                             */
/* ========================================= */
.status-section {
  padding: 0 32px;
  margin: 0 auto 32px auto;
  max-width: 1200px;
  width: calc(100% - 64px);
}

.status-card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.status-pending {
  background: linear-gradient(135deg, #F59E0B 0%, #F97316 100%);
  color: white;
}

.status-approved {
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
  color: white;
}

.status-rejected {
  background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%);
  color: white;
}

.status-completed {
  background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%);
  color: white;
}

.status-icon {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  flex-shrink: 0;
}

.status-title {
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.status-description {
  margin: 0;
  opacity: 0.9;
}

/* ========================================= */
/* REGISTRATION SECTION                       */
/* ========================================= */
.registration-section {
  padding: 0 32px;
  margin: 0 auto 32px auto;
  max-width: 1200px;
  width: calc(100% - 64px);
}

.registration-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  padding: 32px;
}

.registration-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1F2937;
  margin: 0 0 24px 0;
  text-align: center;
}

.toggle-buttons {
  display: flex;
  gap: 12px;
  margin-bottom: 32px;
  border-bottom: 2px solid #E5E7EB;
}

.toggle-btn {
  flex: 1;
  padding: 12px 24px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  font-size: 1rem;
  font-weight: 600;
  color: #6B7280;
  cursor: pointer;
  transition: all 0.3s ease;
}

.toggle-btn.active {
  color: #F2994A;
  border-bottom-color: #F2994A;
}

.toggle-btn:hover {
  color: #F2994A;
}

/* Detail View */
.detail-view {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.detail-section {
  padding: 20px;
  background: #F8F9FA;
  border-radius: 12px;
}

.detail-label {
  font-size: 0.9rem;
  font-weight: 700;
  color: #374151;
  margin-bottom: 8px;
  display: block;
}

.detail-content {
  font-size: 0.95rem;
  color: #6B7280;
  line-height: 1.6;
}

.benefits-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.benefit-box {
  padding: 20px;
  background: linear-gradient(135deg, #4A90E2 0%, #357ABD 100%);
  border-radius: 12px;
  color: white;
}

.benefit-box .detail-label {
  color: white;
  opacity: 0.9;
  margin-bottom: 8px;
}

.benefit-value {
  font-size: 1.2rem;
  font-weight: 700;
}

/* Form View */
.form-view {
  display: flex;
  flex-direction: column;
}

.form-group {
  margin-bottom: 24px;
}

.form-label {
  display: block;
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.form-input,
.form-select {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #E5E7EB;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #F2994A;
  box-shadow: 0 0 0 3px rgba(242, 153, 74, 0.1);
}

.file-input-wrapper {
  position: relative;
}

.file-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px dashed #E5E7EB;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.file-input:hover {
  border-color: #F2994A;
  background: #FFF7ED;
}

.terms-section {
  margin: 24px 0;
  padding: 16px;
  background: #F8F9FA;
  border-radius: 8px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #374151;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #F2994A;
  cursor: pointer;
}

.submit-button {
  width: 100%;
  background: #4A90E2;
  color: white;
  border: none;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.submit-button:hover:not(:disabled) {
  background: #357ABD;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);
}

.submit-button:disabled {
  background: #9CA3AF;
  cursor: not-allowed;
  transform: none;
}

/* WhatsApp Section */
.whatsapp-section {
  margin-top: 20px;
}

.whatsapp-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  background: #25D366;
  color: white;
  text-decoration: none;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.whatsapp-button:hover {
  background: #128C7E;
  transform: translateY(-2px);
  color: white;
  text-decoration: none;
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
}

.whatsapp-button i {
  font-size: 1.5rem;
}

/* ========================================= */
/* ACTION SECTION                             */
/* ========================================= */
.action-section {
  padding: 0 32px;
  margin: 0 auto 32px auto;
  max-width: 1200px;
  width: calc(100% - 64px);
}

.registration-section {
  background: white;
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.terms-checkbox {
  margin-bottom: 20px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #374151;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #F2994A;
}

.register-button {
  width: 100%;
  background: #4A90E2;
  color: white;
  border: none;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.register-button:hover:not(:disabled) {
  background: #357ABD;
  transform: translateY(-2px);
}

.register-button:disabled {
  background: #9CA3AF;
  cursor: not-allowed;
}

.whatsapp-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  background: #25D366;
  color: white;
  text-decoration: none;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.whatsapp-button:hover {
  background: #128C7E;
  transform: translateY(-2px);
  color: white;
  text-decoration: none;
}

.status-button {
  width: 100%;
  background: #F2994A;
  color: white;
  border: none;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.status-button:hover {
  background: #e0874a;
  transform: translateY(-2px);
}

/* ========================================= */
/* TIMELINE SECTION                           */
/* ========================================= */
.timeline-section {
  padding: 0 32px;
  margin: 0 auto 32px auto;
  max-width: 1200px;
  width: calc(100% - 64px);
}

.timeline-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  padding: 24px;
}

.timeline-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1F2937;
  margin: 0 0 20px 0;
  text-align: center;
}

.timeline-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.timeline-item {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  background: #F8F9FA;
  border-radius: 8px;
}

.timeline-label {
  font-weight: 600;
  color: #374151;
}

.timeline-value {
  color: #6B7280;
}

/* ========================================= */
/* PRODUCT KNOWLEDGE SECTION                  */
/* ========================================= */
.product-knowledge-section {
  padding: 0 32px;
  margin: 0 auto 32px auto;
  max-width: 1200px;
  width: calc(100% - 64px);
}

.knowledge-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  padding: 24px;
}

.knowledge-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.knowledge-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1F2937;
  margin: 0;
}

.download-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #4A90E2;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.download-button:hover {
  background: #357ABD;
  transform: translateY(-1px);
}

/* ========================================= */
/* SUBMISSION SECTION                         */
/* ========================================= */
.submission-section {
  padding: 0 32px;
  margin: 0 auto 32px auto;
  max-width: 1200px;
  width: calc(100% - 64px);
}

.submission-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  padding: 32px;
  margin-bottom: 24px;
}

.submission-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1F2937;
  margin: 0 0 24px 0;
}

.form-textarea {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #E5E7EB;
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
  transition: all 0.3s ease;
}

.form-textarea:focus {
  outline: none;
  border-color: #F2994A;
  box-shadow: 0 0 0 3px rgba(242, 153, 74, 0.1);
}

/* ========================================= */
/* FEEDBACK SECTION                           */
/* ========================================= */
.feedback-card {
  background: #FFF7ED;
  border-left: 4px solid #F59E0B;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.feedback-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #92400E;
  margin: 0 0 12px 0;
}

.feedback-content {
  color: #78350F;
  line-height: 1.6;
  white-space: pre-line;
}

/* ========================================= */
/* INFO SECTION                               */
/* ========================================= */
.info-section {
  padding: 0 32px;
  margin: 0 auto 32px auto;
  max-width: 1200px;
  width: calc(100% - 64px);
}

.info-card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  background: linear-gradient(135deg, #F59E0B 0%, #F97316 100%);
  border-radius: 16px;
  color: white;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.info-icon {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  flex-shrink: 0;
}

.info-title {
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.info-description {
  margin: 0;
  opacity: 0.9;
}

/* ========================================= */
/* SUCCESS SECTION                            */
/* ========================================= */
.success-card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
  border-radius: 16px;
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  margin-bottom: 24px;
}

.success-icon {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  flex-shrink: 0;
}

.success-title {
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.success-description {
  margin: 0;
  opacity: 0.9;
}

/* ========================================= */
/* METRICS SECTION                            */
/* ========================================= */
.metrics-section,
.questions-section {
  margin-bottom: 32px;
  padding: 24px;
  background: #F8F9FA;
  border-radius: 12px;
}

.metrics-title,
.questions-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1F2937;
  margin: 0 0 20px 0;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

/* ========================================= */
/* COMPLETION SECTION                         */
/* ========================================= */
.completion-section {
  padding: 0 32px;
  margin: 0 auto 32px auto;
  max-width: 1200px;
  width: calc(100% - 64px);
}

.completion-card {
  background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%);
  border-radius: 16px;
  padding: 48px 32px;
  text-align: center;
  color: white;
  box-shadow: 0 8px 24px rgba(139, 92, 246, 0.3);
}

.completion-icon {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  margin: 0 auto 24px auto;
}

.completion-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.completion-subtitle {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 16px 0;
}

.completion-description {
  font-size: 1rem;
  opacity: 0.9;
  margin: 0 0 32px 0;
  line-height: 1.6;
}

.back-to-campaign-button {
  background: white;
  color: #8B5CF6;
  border: none;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.back-to-campaign-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* ========================================= */
/* MOBILE RESPONSIVE STYLES                  */
/* ========================================= */
@media (max-width: 768px) {
  .campaign-detail-section,
  .content-section,
  .status-section,
  .action-section,
  .registration-section,
  .timeline-section,
  .product-knowledge-section,
  .submission-section,
  .info-section,
  .completion-section {
    width: calc(100% - 40px);
    margin: 0 20px 24px 20px;
    padding: 16px;
  }
  
  .timeline-card,
  .knowledge-card,
  .submission-card {
    padding: 20px;
  }
  
  .timeline-item {
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;
  }
  
  .knowledge-header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .download-button {
    width: 100%;
    justify-content: center;
  }
  
  .metrics-grid {
    grid-template-columns: 1fr;
  }
  
  .info-card,
  .success-card {
    flex-direction: column;
    text-align: center;
  }
  
  .completion-card {
    padding: 32px 20px;
  }
  
  .completion-icon {
    width: 60px;
    height: 60px;
    font-size: 2rem;
  }
  
  .completion-title {
    font-size: 1.5rem;
  }
  
  .completion-subtitle {
    font-size: 1.2rem;
  }
  
  .back-to-campaign-button {
    width: 100%;
    padding: 14px 24px;
    font-size: 1rem;
  }
  
  .registration-card {
    padding: 20px;
  }
  
  .registration-title {
    font-size: 1.3rem;
  }
  
  .toggle-buttons {
    flex-direction: column;
    gap: 0;
  }
  
  .toggle-btn {
    padding: 12px 16px;
    text-align: center;
  }
  
  .benefits-row {
    grid-template-columns: 1fr;
  }
  
  .form-input,
  .form-select,
  .file-input,
  .form-textarea {
    font-size: 0.9rem;
  }
  
  .submit-button {
    font-size: 1rem;
    padding: 14px 20px;
  }
  
  .whatsapp-button {
    font-size: 1rem;
    padding: 14px 20px;
  }
  
  .campaign-header {
    flex-direction: column;
    gap: 20px;
  }
  
  .campaign-image-container {
    width: 100%;
    height: 200px;
  }
  
  .campaign-title {
    font-size: 1.5rem;
  }
  
  .detail-item {
    padding: 12px 16px;
  }
  
  .content-title {
    padding: 16px 20px;
    font-size: 1.1rem;
  }
  
  .content-body {
    padding: 20px;
  }
  
  .info-row {
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;
  }
  
  .info-value {
    text-align: left;
  }
  
  .benefits-grid {
    grid-template-columns: 1fr;
  }
  
  .status-card {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }
  
  .status-title {
    font-size: 1.2rem;
  }
}

/* ========================================= */
/* DETAIL CONTENT FORMATTING                 */
/* ========================================= */
.detail-content {
  line-height: 1.8;
  color: #374151;
  font-size: 0.95rem;
  white-space: pre-wrap;
  word-wrap: break-word;
  margin-top: 8px;
}

.detail-content::v-deep br {
  display: block;
  content: "";
  margin: 0.5em 0;
}
</style>